'use client';

import Link from 'next/link';

import { SmallCardProps, SmallCardType } from './types';

import IconLinkButton from '@/assets/icons/iconLinkButton.svg';
import { AppImageClient } from '@/shared/components/appImage';
import { Button } from '@/shared/components/button';
import { ScrollToFormButton } from '@/shared/components/scrollToFormButton';
import { useUtmHref } from '@/shared/hooks';
import { classNames } from '@/shared/lib/classNames/classNames';

/**
 * @deprecated Этот компонент устарел. Используйте SmallCard из components/card вместо него.
 */
export const SmallCard = ({
  descriptionId,
  description,
  subDescriptionId,
  subDescription,
  href,
  imageSrc,
  imageData,
  countRow,
  type,
  buttonContent,
  buttonLink,
  alt,
  smoothScroll = false,
  isAnimateLinkIcon = false,
  customClassNames = {},
}: SmallCardProps) => {
  const hrefWithUtm = useUtmHref(href);
  const buttonLinkWithUtm = useUtmHref(buttonLink);

  const content = (
    <div
      className={classNames({
        cls: 'rounded-20 flex h-full w-full flex-col px-7 pt-6 pb-7 max-sm:px-5 max-sm:py-6',
        mods: {
          'from-white/20 to-white/5 hover:inset-0 hover:bg-linear-to-b': href,
          'justify-end': buttonLink,
          'justify-between': !buttonLink || type === SmallCardType.BLURRED,
          group: isAnimateLinkIcon,
        },
        additional: [customClassNames.contentContainer],
      })}
    >
      <div
        className={classNames({
          cls: 'flex justify-between',
          additional: [customClassNames.img],
        })}
      >
        {imageData ? (
          <AppImageClient
            {...imageData}
            width={96}
            height={96}
            alt={alt || ''}
          />
        ) : (
          imageSrc && (
            <img
              src={imageSrc}
              width={96}
              height={96}
              alt={alt || ''}
              decoding="async"
              loading="lazy"
            />
          )
        )}
        {href && (
          <div className="bg-custom-surface-contrast-low text-text-base-primary group-hover:bg-surface-base-inverse-primary flex h-12 w-12 justify-between rounded-xl p-3 transition-colors duration-300">
            <IconLinkButton className="transition-transform duration-300 group-hover:rotate-45" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 max-md:gap-3">
        <p
          id={descriptionId}
          className={classNames({
            cls: 'max-sm:text-sm [&>a]:cursor-pointer [&>a]:underline',
            mods: {
              'text-[30px] leading-[38px] font-medium tracking-[-.01em]':
                type === SmallCardType.BLURRED,
            },
            additional: [customClassNames.content],
          })}
          dangerouslySetInnerHTML={{ __html: description! }}
        ></p>
        {!!subDescription && (
          <p
            id={subDescriptionId}
            className={classNames({
              cls: 'tracking-[-.01em] max-sm:text-sm [&>a]:cursor-pointer [&>a]:underline [&>a]:underline-offset-2',
              mods: {
                'text-[16px] leading-[24px] tracking-[-.01em]':
                  type === SmallCardType.BLURRED,
              },
              additional: [customClassNames.subDescription],
            })}
            dangerouslySetInnerHTML={{ __html: subDescription! }}
          ></p>
        )}
      </div>

      {buttonLinkWithUtm ? (
        smoothScroll ? (
          <ScrollToFormButton
            variant="primary"
            className="mt-6 flex w-full items-center justify-center max-sm:mt-4 max-sm:h-14"
          >
            {buttonContent}
          </ScrollToFormButton>
        ) : (
          <Button
            className="flex h-14 w-full items-center justify-center max-sm:w-full"
            href={buttonLinkWithUtm}
            target="_blank"
            variant="primary"
            ariaLabel={buttonContent}
          >
            {buttonContent}
          </Button>
        )
      ) : null}
    </div>
  );

  const commonClassName = classNames({
    cls: 'block relative h-[304px] overflow-hidden rounded-20 bg-surface-base-secondary max-sm:h-[236px] max-sm:w-full',
    mods: {
      'cursor-pointer transition-transform duration-300 hover:scale-[1.02]':
        href,
      'h-[304px] w-[368px]': countRow === 3,
      'h-[280px] w-[268px]': countRow === 4,
      'h-[320px] w-[348px] bg-custom-surface-contrast-mid backdrop-blur-lg':
        type === SmallCardType.BLURRED,
    },
    additional: [customClassNames.main],
  });

  if (hrefWithUtm) {
    return (
      <Link href={hrefWithUtm} target="_blank" className={commonClassName}>
        {content}
      </Link>
    );
  }

  return <section className={commonClassName}>{content}</section>;
};
